import { DEG } from './mathx.js';

/**
 * Weapon data lives in `defs/<id>.js`, one file per weapon (see
 * `defs/index.js` and `CONTRACT.md`) — this file keeps only what every
 * weapon shares: throwables, the recoil-pattern generator and the spread
 * modifiers.
 *
 * Recoil is split in two, exactly as a modern shooter does it:
 *   - `pattern`  a DETERMINISTIC per-shot camera climb a player can memorise
 *                and counter. Generated once from a fixed seed.
 *   - `spread`   a random cone that grows with sustained fire and shrinks when
 *                aiming, crouched or still. This is the part you cannot learn.
 */

/**
 * Throwables — frag grenade and flashbang. Not part of `WEAPON_DEFS`/the
 * weapon-slot cycle: they throw from whatever gun is currently equipped
 * (see `WeaponSystem.throwFrag`/`throwFlash`), the same way `grenade`/`use`
 * are their own input actions rather than weapon-switch keys.
 */
export const THROWABLE_DEFS = {
  frag: {
    id: 'frag',
    label: 'Frag Grenade',
    fuse: 2.2,
    throwSpeed: 15,
    radius: 6.5,
    damage: 120,
    startCount: 2,
  },
  flash: {
    id: 'flash',
    label: 'Flashbang',
    fuse: 1.6,
    throwSpeed: 16,
    radius: 9,
    damage: 0,
    startCount: 1,
  },
};

/**
 * Generate the deterministic recoil pattern for a weapon.
 *
 * The shape is what a player learns: a strong vertical climb for the first few
 * shots, then the vertical settles while the muzzle starts to wander sideways
 * in a smooth, repeatable S. Everything comes from one fixed seed so the same
 * weapon always kicks the same way — including in capture mode.
 *
 * @returns {Float32Array} pairs of [pitch, yaw] in radians, length n*2.
 */
export function buildRecoilPattern(def, Rng) {
  const r = def.recoil;
  const n = r.patternLength;
  const rng = new Rng(r.patternSeed);
  const out = new Float32Array(n * 2);
  // Two out-of-phase wanders make the horizontal read as a learnable snake
  // rather than as noise.
  const phase = rng.float() * Math.PI * 2;
  const phase2 = rng.float() * Math.PI * 2;
  const bias = rng.signed() * 0.35;
  for (let i = 0; i < n; i++) {
    const shot = i;
    const climb = r.climbShape[Math.min(shot, r.climbShape.length - 1)];
    // Vertical: strong early, tapering, with a per-shot signature bump.
    const sig = 0.88 + rng.float() * 0.24;
    out[i * 2] = r.pitch * climb * sig;
    // Horizontal: a smooth snake plus a fixed per-shot signature.
    const t = i / Math.max(1, n - 1);
    const snake =
      Math.sin(phase + t * Math.PI * 2.6) * 0.75 + Math.sin(phase2 + t * Math.PI * 5.1) * 0.35;
    out[i * 2 + 1] = r.yaw * (snake * r.drift * 3.2 + bias + rng.signed() * 0.25);
  }
  return out;
}

export const SPREAD_MODS = {
  crouch: 0.78,
  prone: 0.6,
  still: 0.82,
  walking: 1.15,
  sprinting: 2.2,
  airborne: 2.0,
  hipfire: 1,
};

export const DEG2RAD = DEG;
